/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
*/

import { token } from "@panda/tokens";

/**
 * Resolves a color value from various input formats to a CSS color string.
 * Useful for Cosmos components that accept multiple color formats for flexibility.
 *
 * This utility is specific to Cosmos components and EDS tokens.
 *
 * @param color - Color value in one of three formats:
 *   - Panda token path (e.g., "eds.background.elevated.low.default")
 *   - Enum value (e.g., "ElevatedLowDefault")
 *   - CSS color (e.g., "#1a1d22", "rgb(26, 29, 34)", "hsl(216, 13%, 12%)")
 *   - null/undefined (uses defaultColor)
 * @param defaultColor - Fallback color if resolution fails (defaults to "eds.background.default")
 * @returns Resolved CSS color string
 *
 * @example
 * ```tsx
 * import { resolvePandaColor } from "@components/utils";
 *
 * // Using token path
 * const color1 = resolvePandaColor("eds.background.elevated.low.default");
 *
 * // Using enum value
 * const color2 = resolvePandaColor("ElevatedLowDefault");
 *
 * // Using CSS color
 * const color3 = resolvePandaColor("#1a1d22");
 *
 * // With custom default
 * const color4 = resolvePandaColor(undefined, "eds.background.elevated.low.default");
 *
 * // Works with optional chaining (no ?? undefined needed)
 * const color5 = resolvePandaColor(config?.backgroundColor);
 * ```
 */
export const resolvePandaColor = (
	color: string | null | undefined,
	defaultColor: string = "eds.background.default",
): string => {
	if (!color) {
		return token(`colors.${defaultColor}` as any);
	}

	// CSS color value - use as-is
	if (color.startsWith("#") || color.startsWith("rgb") || color.startsWith("hsl")) {
		return color;
	}

	try {
		// Already a token path like "eds.background.elevated.low.default"
		if (color.includes(".")) {
			const tokenPath = `colors.${color}`;
			return token(tokenPath as any);
		}

		// Enum value like "ElevatedLowDefault" - convert to kebab-case
		const converted = color
			.replace(/([A-Z])/g, ".$1")
			.toLowerCase()
			.replace(/^\./, "");
		const tokenPath = `colors.eds.background.${converted}`;
		return token(tokenPath as any);
	} catch (error) {
		// If token resolution fails, fall back to default
		console.warn(`Failed to resolve gradient color token: ${color}`, error);
		return token(`colors.${defaultColor}` as any);
	}
};
